<template>
    <div class="TeamChallangeUserView">
        <div class="top-wrapper">
            <div class="left-side">
                <img src="@/assets/images/theory_image.png" />
                <div class="headers">
                    <h4>
                        {{ course.lifeskillNumber }} -
                        {{ $t(`dashboard.general.lifeskillLabels.${course.lifeskillNumber}`) }}
                    </h4>
                    <h3>{{ journey.name }}</h3>
                </div>
            </div>
            <div v-if="!isQuizOrGameFinished" class="right-side"> Team quiz starts {{ timeToStart }} </div>
            <div v-else class="top-players">
                <div v-if="gameTopPlayer && isGameFinished" class="top-game-player">
                    <h5>TOP Game</h5>
                    <div class="avatar-and-name">
                        <img class="golden-rank-icn" src="@/assets/images/golden-rank-icn.png" alt="golden-rank-icon" />
                        <img src="https://picsum.photos/200" :alt="gameTopPlayer.lastName" />
                        <p>{{ gameTopPlayer.firstName }} {{ gameTopPlayer.lastName || '' }}</p>
                    </div>
                </div>

                <div v-if="quizTopPlayer && isQuizFinished" class="top-quiz-player">
                    <h5>TOP Quiz</h5>
                    <div class="avatar-and-name">
                        <img class="golden-rank-icn" src="@/assets/images/golden-rank-icn.png" alt="golden-rank-icon" />
                        <img src="https://picsum.photos/200" :alt="quizTopPlayer.lastName" />
                        <p>{{ quizTopPlayer.firstName }} {{ quizTopPlayer.lastName || '' }}</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="bottom-wrapper">
            <div class="left-side">
                <p v-if="!completedStepsCount">
                    Steps 0 of {{ journey.stepIds.length }} <span @click="goToCourseTheory">- Start Learning </span>
                </p>
                <p v-else-if="completedStepsCount !== journey.stepIds.length">
                    Steps {{ completedStepsCount }} of {{ journey.stepIds.length }} <span>- Continue Learning </span>
                </p>
                <p v-else> Steps {{ completedStepsCount }} of {{ journey.stepIds.length }} Completed </p>
                <div class="progress-bar-wrapper">
                    <div class="progress-bar" :style="{ width: percentageProgress }" />
                </div>
            </div>
            <div class="right-side">
                <div class="buttons">
                    <button v-if="isGameInProgress" :disabled="true">In progress ...</button>
                    <button v-else-if="!isGameFinished" :disabled="!isGameUnlocked" @click="gotoGame">
                        Attend Game
                    </button>

                    <button v-else class="game-leaderboard-btn" @click="openLeaderBoardPopup(game.id)">
                        Game Leaderboard
                    </button>
                    <button v-if="!isQuizFinished" :disabled="!isQuizUnlocked" @click="gotoQuiz">Attend Quiz</button>
                    <button v-else class="game-leaderboard-btn" @click="openLeaderBoardPopup(quiz.id)">
                        Quiz Leaderboard
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import moment from 'moment';

import LeaderBoard from './LeaderBoard';

export default {
    props: {
        teamGoal: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            gameLeaderBoard: [],
            quizLeaderBoard: []
        };
    },

    computed: {
        ...mapGetters({
            getJourneyById: 'moduleCourse/getJourneyById',
            getStepById: 'moduleCourse/getStepById'
        }),

        course() {
            return this.$store.state.moduleCourse.courses[this.journey?.courseId];
        },

        journey() {
            return this.getJourneyById(this.teamGoal.journeyId);
        },

        timeToStart() {
            const now = moment();
            const startDate = moment(this.teamGoal.stop);

            return now.to(startDate);
        },

        completedStepsCount() {
            return this.journey.stepIds
                .map(stepId => this.getStepById(stepId))
                .reduce((accumulator, currentValue) => accumulator + currentValue.progress, 0);
        },

        percentageProgress() {
            return Math.round((this.completedStepsCount / this.journey.stepIds.length) * 100) + '%';
        },

        challengeStarted() {
            const now = moment();
            const endDate = moment(this.teamGoal.stop);

            return endDate.diff(now) < 0;
        },

        isGameUnlocked() {
            return this.challengeStarted && this.publishedGames.find(game => game.name === 'game');
        },

        isQuizUnlocked() {
            return this.challengeStarted && this.publishedGames.find(game => game.name === 'Quiz game');
        },

        game() {
            return this.publishedGames.find(game => game.name === 'game');
        },

        isGameFinished() {
            return this.game && this.game.startedAt && !this.game.running;
        },

        isGameInProgress() {
            return this.game && this.game.running;
        },

        quiz() {
            return this.publishedGames.find(game => game.name === 'Quiz game');
        },

        isQuizFinished() {
            return this.quiz && this.quiz.startedAt && !this.quiz.running;
        },

        games() {
            return this.$store.getters['moduleMemoransier/games'].filter(game => {
                const options = JSON.parse(game.options);

                return (
                    options.teamId === this.teamGoal.teamId &&
                    options.courseId === this.course.id &&
                    options.stepGroupId === this.teamGoal.journeyId
                );
            });
        },

        publishedGames() {
            return this.games.filter(game => game.published);
        },

        isQuizOrGameFinished() {
            return this.isGameFinished || this.isQuizFinished;
        },

        gameTopPlayer() {
            return this.gameLeaderBoard && this.gameLeaderBoard.length && this.gameLeaderBoard[0];
        },

        quizTopPlayer() {
            return this.quizLeaderBoard && this.quizLeaderBoard.length && this.quizLeaderBoard[0];
        }
    },

    mounted() {
        this.intervalId = setInterval(async () => {
            await this.refreshGame();
            await this.refreshLeaderBoard();
        }, 5000);
    },

    beforeDestroy() {
        clearInterval(this.intervalId);
    },

    methods: {
        goToCourseTheory() {
            this.$router.push({
                name: 'DashboardLifeskillViewWithTab',
                params: { lifeskillId: this.course.lifeskillNumber, tabName: 'theory' }
            });
        },

        gotoGame() {
            const game = this.publishedGames.find(game => game.name === 'game');

            this.$router.push({
                name: 'MemoransierGameView',
                params: { gameId: game.id }
            });
        },

        gotoQuiz() {
            const quiz = this.publishedGames.find(game => game.name === 'Quiz game');

            this.$router.push({
                name: 'MemoransierGameView',
                params: { gameId: quiz.id }
            });
        },

        async refreshGame() {
            await this.$store.dispatch('moduleMemoransier/refresh');
        },

        async refreshLeaderBoard() {
            this.gameLeaderBoard =
                this.game && (await this.$store.dispatch('moduleMemoransier/getLeaderboard', this.game.id));
            this.quizLeaderBoard =
                this.quiz && (await this.$store.dispatch('moduleMemoransier/getLeaderboard', this.quiz.id));
        },

        openLeaderBoardPopup(gameId) {
            this.$modal.open({
                component: LeaderBoard,
                props: {
                    gameId,
                    teamGoal: this.teamGoal
                },
                target: 'app-modal'
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.TeamChallangeUserView {
    background: #18194c;
    box-shadow: 0px 2px 23px rgba(79, 79, 79, 0.1);
    border-radius: 8px;
    padding: 30px;
    .top-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        .left-side {
            display: flex;
            img {
                width: 52px;
                height: 52px;
            }
            .headers {
                margin-left: 20px;
                h4 {
                    font-weight: normal;
                    font-size: 14px;
                    line-height: 22px;
                    color: #758ebf;
                    margin: 0;
                    margin-bottom: 10px;
                }

                h3 {
                    font-style: normal;
                    font-weight: 500;
                    font-size: 22px;
                    line-height: 26px;
                    color: rgba(255, 255, 255, 0.8);
                    padding-right: 50px;
                    margin: 0;
                }
            }
        }

        .right-side {
            font-style: normal;
            font-weight: 500;
            font-size: 16px;
            line-height: 19px;
            color: #40ace0;
        }

        .top-players {
            display: flex;
            .top-game-player {
                margin-right: 50px;
                h5 {
                    font-style: normal;
                    font-weight: bold;
                    font-size: 14px;
                    line-height: 15px;
                    color: #48546f;
                    margin-bottom: 10px;
                    margin-top: 0;
                }
                .avatar-and-name {
                    display: flex;
                    align-items: center;
                    .golden-rank-icn {
                        width: 27px;
                        height: 27px;
                        margin-right: 10px;
                    }
                    img {
                        width: 34px;
                        height: 34px;
                        border-radius: 50%;
                    }
                    p {
                        font-style: normal;
                        font-weight: 600;
                        font-size: 14px;
                        line-height: 16px;
                        margin: 0 0 0 10px;
                        color: rgba(255, 255, 255, 0.8);
                    }
                }
            }

            .top-quiz-player {
                h5 {
                    font-style: normal;
                    font-weight: bold;
                    font-size: 14px;
                    line-height: 15px;
                    color: #48546f;
                    margin-bottom: 10px;
                    margin-top: 0;
                }
                .avatar-and-name {
                    display: flex;
                    align-items: center;
                    .golden-rank-icn {
                        width: 27px;
                        height: 27px;
                        margin-right: 10px;
                    }
                    img {
                        width: 34px;
                        height: 34px;
                        border-radius: 50%;
                    }
                    p {
                        font-style: normal;
                        font-weight: 600;
                        font-size: 14px;
                        line-height: 16px;
                        margin: 0 0 0 10px;
                        color: rgba(255, 255, 255, 0.8);
                    }
                }
            }
        }
    }

    .bottom-wrapper {
        padding: 100px 0 0 0;
        display: flex;
        justify-content: space-between;
        .left-side {
            display: flex;
            align-items: center;
            p {
                font-weight: 500;
                font-size: 16px;
                line-height: 19px;
                color: rgba(139, 208, 224, 0.5);
                span {
                    color: #40ace0;
                    cursor: pointer;
                }
            }
            .progress-bar-wrapper {
                margin-left: 30px;
                width: 96px;
                height: 6px;
                background: rgba(89, 131, 247, 0.14);
                border-radius: 55px;
                .progress-bar {
                    background: #40ace0;
                    height: 100%;
                    border-radius: 55px;
                }
            }
        }

        .right-side {
            button {
                background: #40ace0;
                box-sizing: border-box;
                border-radius: 5px;
                border: none;
                width: 188px;
                height: 56px;
                font-weight: 500;
                font-size: 16px;
                line-height: 19px;
                color: #ffffff;
                margin-left: 10px;
                cursor: pointer;
                outline: none;
                &:disabled {
                    opacity: 0.3;
                }
            }
            .game-leaderboard-btn {
                background: #18194c;
                box-shadow: 0px 2px 23px rgba(79, 79, 79, 0.1);
                border-radius: 8px;
                border: 1px solid #346fc8;
                color: #346fc8;
                cursor: pointer;
                outline: none;
            }
        }
    }
}
</style>
